import { TextInput } from '@frontend/components/basic-forms';
import { CommonMessage, getCorrectTranslation, useLocale } from '@frontend/lang';
import { LuCheck } from 'react-icons/lu';
import { FormattedMessage, useIntl } from 'react-intl';

import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import useVerifyContactInformation from './verify-contact-information.controller';

const ID = 'verify-contact-information';

const VerifyContactInformation = () => {
    const intl = useIntl();
    const { locale } = useLocale();
    const viewProps = useVerifyContactInformation();
    const serviceName = viewProps.selectedServiceProduct
        ? getCorrectTranslation(locale, viewProps.selectedServiceProduct, 'name') || viewProps.selectedServiceProduct.name
        : null;

    if (viewProps.submitted) {
        return (
            <WizardScreenContainer
                id={ID}
                footer={{
                    onSubmit: viewProps.onContinue,
                    submitLabel: viewProps.continueEnabled ? CommonMessage.BUTTONS.CONTINUE : CommonMessage.BUTTONS.EXIT
                }}>
                <div className='flex h-full flex-col items-center justify-center gap-4 text-center'>
                    <div className='bg-status-success relative flex size-24 items-center justify-center rounded-full'>
                        <LuCheck className='text-always-white size-16' />
                    </div>

                    <div className='text-foreground-default text-xl font-semibold'>
                        <FormattedMessage
                            id='kiosk.wizard.verify-contact-information.request-submitted-title'
                            description='Title shown after a kiosk service request order has been created'
                            defaultMessage='Your request has been submitted'
                        />
                    </div>
                    <div className='text-brand-muted max-w-xl text-base font-medium'>
                        {serviceName ? (
                            <FormattedMessage
                                id='kiosk.wizard.verify-contact-information.request-submitted-service-message'
                                description='Message shown after a kiosk service request order has been created, including the selected service name'
                                defaultMessage='We will contact you about {serviceName}.'
                                values={{ serviceName }}
                            />
                        ) : (
                            <FormattedMessage
                                id='kiosk.wizard.verify-contact-information.request-submitted-message'
                                description='Message shown after a kiosk service request order has been created without a service name'
                                defaultMessage='We will contact you about your request.'
                            />
                        )}
                    </div>
                </div>
            </WizardScreenContainer>
        );
    }

    return (
        <WizardScreenContainer
            id={ID}
            footer={{
                onSubmit: viewProps.onSubmit,
                submitDisabled: viewProps.submitDisabled,
                submitLabel: CommonMessage.BUTTONS.SUBMIT
            }}
            loadingModal={{
                show: viewProps.isSubmitting,
                message: (
                    <FormattedMessage
                        id='kiosk.wizard.verify-contact-information.submitting-message'
                        description='Loading message shown while the kiosk is creating a service request order'
                        defaultMessage='Submitting your request...'
                    />
                )
            }}
            errorModal={{
                show: !!viewProps.error,
                title: CommonMessage.ERRORS.DEFAULT,
                message: viewProps.error,
                onClose: viewProps.resetError
            }}
            containerClassName='bg-background-default flex flex-1 flex-col items-start justify-start gap-4 self-stretch overflow-y-auto px-5 pt-6 pb-20'>
            <div className='text-foreground-default justify-start text-base font-medium'>
                <FormattedMessage
                    id='kiosk.wizard.verify-contact-information.instructions'
                    description='Instruction explaining that either phone number or email must be provided'
                    defaultMessage='Enter your phone number or email address so we can contact you about your request.'
                />
            </div>

            <div className='flex w-full flex-col gap-4'>
                <TextInput
                    id='verify-contact-information-phone-number'
                    className='h-auto'
                    label={
                        <FormattedMessage
                            id='kiosk.wizard.verify-contact-information.phone-number'
                            description='Label for the phone number input on the contact information screen'
                            defaultMessage='Phone number'
                        />
                    }
                    value={viewProps.phoneNumber}
                    onChange={viewProps.setPhoneNumber}
                    inputMode='tel'
                    isClearable
                    placeholder={intl.formatMessage({
                        id: 'kiosk.wizard.verify-contact-information.phone-number-placeholder',
                        description: 'Placeholder for the phone number input on the contact information screen',
                        defaultMessage: '+32 470 12 34 56'
                    })}
                />

                <TextInput
                    id='verify-contact-information-email'
                    className='h-auto'
                    label={
                        <FormattedMessage
                            id='kiosk.wizard.verify-contact-information.email'
                            description='Label for the email input on the contact information screen'
                            defaultMessage='Email'
                        />
                    }
                    value={viewProps.email}
                    onChange={viewProps.setEmail}
                    inputMode='email'
                    isClearable
                    placeholder={intl.formatMessage({
                        id: 'kiosk.wizard.verify-contact-information.email-placeholder',
                        description: 'Placeholder for the email input on the contact information screen',
                        defaultMessage: 'name@example.com'
                    })}
                />
            </div>
        </WizardScreenContainer>
    );
};

export default VerifyContactInformation;
